import { Link } from 'react-router-dom';
import { 
  MapPin, Phone, Mail, ArrowRight,
  Facebook, Instagram, Linkedin, Send, Sparkles, Globe, GraduationCap
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/g96w8w8c_HE%20Direct%20logo%202023.png";
const ICEF_LOGO = "https://customer-assets.emergentagent.com/job_globalcampus-2/artifacts/uybd5yuu_001bG000002DXv5QAG_badge.png";
const WHATSAPP_NUMBER = '923268096371';
const FACEBOOK_URL = 'https://www.facebook.com/share/1QFSsrjUfE/?mibextid=wwXIfr';

// OPTION A: Clean White with Coral/Peach Accents - Fresh & Inviting
export const FooterOptionA = () => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}`;

  return (
    <footer className="bg-white border-t border-gray-100">
      {/* CTA Banner - Soft gradient */}
      <div className="bg-gradient-to-r from-rose-50 via-orange-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-rose-400 to-orange-400 rounded-2xl flex items-center justify-center shadow-lg shadow-rose-200">
                <Sparkles className="w-6 h-6 text-gray-800" />
              </div>
              <div>
                <h3 className="text-xl font-semibold text-gray-800">Ready to Start Your Journey?</h3>
                <p className="text-gray-500 text-sm">Get expert guidance for your study abroad dreams</p>
              </div>
            </div>
            <div className="flex gap-3">
              <Button asChild className="bg-gradient-to-r from-rose-500 to-orange-500 hover:from-rose-600 hover:to-orange-600 text-gray-800 font-semibold shadow-lg shadow-rose-200 rounded-full px-6">
                <Link to="/register">Apply Now <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild variant="outline" className="border-2 border-rose-200 text-rose-600 hover:bg-rose-50 rounded-full px-6">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">WhatsApp Us</a>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Brand */}
          <div className="lg:col-span-4 space-y-4">
            <img src={LOGO_URL} alt="H E Direct" className="h-10 w-auto" />
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
              Your trusted partner for international education. Connecting students with world-class universities since 2015.
            </p>
            <div className="flex items-center gap-6">
              <img src={ICEF_LOGO} alt="ICEF Certified" className="h-12 w-auto" />
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-rose-500 font-semibold text-sm uppercase tracking-wider mb-4">Explore</h4>
            <ul className="space-y-2">
              {['Courses', 'Destinations', 'Scholarships', 'Blog'].map(item => (
                <li key={item}>
                  <Link to={`/${item.toLowerCase()}`} className="text-gray-500 hover:text-rose-500 text-sm transition-colors">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-rose-500 font-semibold text-sm uppercase tracking-wider mb-4">Services</h4>
            <ul className="space-y-2">
              {['Student Registration', 'Agent Partnership', 'Eligibility Check', 'Finance Options'].map(item => (
                <li key={item}>
                  <Link to="/register" className="text-gray-500 hover:text-rose-500 text-sm transition-colors">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h4 className="text-rose-500 font-semibold text-sm uppercase tracking-wider mb-4">Get in Touch</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-3 text-gray-500 text-sm">
                <div className="w-8 h-8 bg-rose-100 rounded-full flex items-center justify-center">
                  <Phone className="w-4 h-4 text-rose-500" />
                </div>
                <a href="tel:+923268096371" className="hover:text-rose-500 transition-colors">+92 326 8096371</a>
              </li>
              <li className="flex items-center gap-3 text-gray-500 text-sm">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center">
                  <Mail className="w-4 h-4 text-orange-500" />
                </div>
                <a href="mailto:info@hedirect.com" className="hover:text-rose-500 transition-colors">info@hedirect.com</a>
              </li>
              <li className="flex items-start gap-3 text-gray-500 text-sm">
                <div className="w-8 h-8 bg-amber-100 rounded-full flex items-center justify-center mt-0.5">
                  <MapPin className="w-4 h-4 text-amber-500" />
                </div>
                <span>Peshawar, Pakistan | Birmingham, UK</span>
              </li>
            </ul>
            <div className="flex items-center gap-3 mt-4">
              <a href={FACEBOOK_URL} className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:bg-rose-500 hover:text-gray-800 transition-all">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:text-gray-800 transition-all">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-gray-800 transition-all">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-100 bg-[#f8f9fa]/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-sm text-gray-400">
            <p>© 2024 H E Direct Consultants. All rights reserved.</p>
            <div className="flex gap-4">
              <Link to="/help" className="hover:text-rose-500 transition-colors">Privacy Policy</Link>
              <Link to="/help" className="hover:text-rose-500 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// OPTION B: White with Mint/Teal - Modern & Refreshing
export const FooterOptionB = () => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}`;

  return (
    <footer className="bg-gradient-to-b from-white to-slate-50">
      {/* Wave Pattern CTA */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-teal-400 via-cyan-400 to-emerald-400 opacity-10"></div>
        <div className="relative bg-white/80 backdrop-blur-sm border-y border-teal-100">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              <div className="flex items-center gap-4">
                <div className="relative">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-cyan-500 rounded-3xl flex items-center justify-center shadow-xl shadow-teal-200/50 rotate-3">
                    <Globe className="w-7 h-7 text-gray-800" />
                  </div>
                  <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-emerald-400 rounded-full border-2 border-white"></div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-800">Start Your Global Journey</h3>
                  <p className="text-gray-500 text-sm">Connect with top universities worldwide</p>
                </div>
              </div>
              <div className="flex gap-3">
                <Button asChild className="bg-gradient-to-r from-teal-500 to-cyan-500 hover:from-teal-600 hover:to-cyan-600 text-gray-800 font-semibold shadow-lg shadow-teal-200 rounded-xl px-6">
                  <Link to="/register">Get Started <ArrowRight className="w-4 h-4 ml-2" /></Link>
                </Button>
                <Button asChild variant="outline" className="border-2 border-teal-200 text-teal-600 hover:bg-teal-50 rounded-xl px-6">
                  <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">Chat Now</a>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Brand */}
          <div className="lg:col-span-4 space-y-4">
            <img src={LOGO_URL} alt="H E Direct" className="h-10 w-auto" />
            <p className="text-gray-500 text-sm leading-relaxed max-w-xs">
              Empowering students to achieve their international education dreams with personalized guidance.
            </p>
            <div className="p-3 bg-gradient-to-r from-teal-50 to-cyan-50 rounded-xl inline-block">
              <img src={ICEF_LOGO} alt="ICEF Certified" className="h-12 w-auto" />
            </div>
          </div>

          {/* Quick Links */}
          <div className="lg:col-span-2">
            <h4 className="text-teal-600 font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-teal-500 rounded-full"></span>
              Explore
            </h4>
            <ul className="space-y-2">
              {['Courses', 'Destinations', 'Scholarships', 'Blog'].map(item => (
                <li key={item}>
                  <Link to={`/${item.toLowerCase()}`} className="text-gray-500 hover:text-teal-600 text-sm transition-colors hover:translate-x-1 inline-block">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div className="lg:col-span-2">
            <h4 className="text-cyan-600 font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-cyan-500 rounded-full"></span>
              Services
            </h4>
            <ul className="space-y-2">
              {['Student Registration', 'Agent Partnership', 'Eligibility Check', 'Finance Options'].map(item => (
                <li key={item}>
                  <Link to="/register" className="text-gray-500 hover:text-cyan-600 text-sm transition-colors hover:translate-x-1 inline-block">
                    {item}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="lg:col-span-4">
            <h4 className="text-emerald-600 font-semibold text-sm uppercase tracking-wider mb-4 flex items-center gap-2">
              <span className="w-1.5 h-1.5 bg-emerald-500 rounded-full"></span>
              Get in Touch
            </h4>
            <div className="bg-gradient-to-br from-white to-slate-50 rounded-2xl p-4 border border-slate-100 shadow-sm">
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <Phone className="w-4 h-4 text-teal-500" />
                  <a href="tel:+923268096371" className="hover:text-teal-600 transition-colors">+92 326 8096371</a>
                </li>
                <li className="flex items-center gap-3 text-gray-600 text-sm">
                  <Mail className="w-4 h-4 text-cyan-500" />
                  <a href="mailto:info@hedirect.com" className="hover:text-cyan-600 transition-colors">info@hedirect.com</a>
                </li>
                <li className="flex items-start gap-3 text-gray-600 text-sm">
                  <MapPin className="w-4 h-4 text-emerald-500 mt-0.5" />
                  <span>Peshawar, PK | Birmingham, UK</span>
                </li>
              </ul>
            </div>
            <div className="flex items-center gap-3 mt-4">
              <a href={FACEBOOK_URL} className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-gray-400 hover:bg-blue-500 hover:text-gray-800 transition-all hover:-translate-y-1">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-gray-400 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:text-gray-800 transition-all hover:-translate-y-1">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:text-gray-800 transition-all hover:-translate-y-1">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row items-center justify-between gap-2 text-sm text-gray-400">
            <p>© 2024 H E Direct Consultants. All rights reserved.</p>
            <div className="flex gap-4">
              <Link to="/help" className="hover:text-teal-500 transition-colors">Privacy Policy</Link>
              <Link to="/help" className="hover:text-teal-500 transition-colors">Terms of Service</Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

// OPTION C: White with Purple/Violet Gradients - Playful & Modern
export const FooterOptionC = () => {
  const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}`;

  return (
    <footer className="bg-white">
      {/* Fun Stats Banner */}
      <div className="bg-gradient-to-r from-violet-50 via-purple-50 to-fuchsia-50 border-y border-purple-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-wrap items-center justify-center gap-8">
            <div className="text-center group cursor-default">
              <p className="text-3xl font-semibold bg-gradient-to-r from-violet-600 to-purple-600 bg-clip-text text-transparent group-hover:scale-110 transition-transform">10,000+</p>
              <p className="text-gray-500 text-xs">Happy Students</p>
            </div>
            <div className="h-8 w-px bg-purple-200" />
            <div className="text-center group cursor-default">
              <p className="text-3xl font-semibold bg-gradient-to-r from-purple-600 to-fuchsia-600 bg-clip-text text-transparent group-hover:scale-110 transition-transform">100+</p>
              <p className="text-gray-500 text-xs">Partner Universities</p>
            </div>
            <div className="h-8 w-px bg-purple-200" />
            <div className="text-center group cursor-default">
              <p className="text-3xl font-semibold bg-gradient-to-r from-fuchsia-600 to-pink-600 bg-clip-text text-transparent group-hover:scale-110 transition-transform">15+</p>
              <p className="text-gray-500 text-xs">Countries</p>
            </div>
            <div className="h-8 w-px bg-purple-200" />
            <img src={ICEF_LOGO} alt="ICEF Certified" className="h-12 w-auto opacity-70 hover:opacity-100 transition-opacity" />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          {/* Brand & CTA */}
          <div className="lg:col-span-5 space-y-6">
            <img src={LOGO_URL} alt="H E Direct" className="h-12 w-auto" />
            <p className="text-gray-500 leading-relaxed">
              Your gateway to world-class education. We connect ambitious students with prestigious universities across the globe.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button asChild size="lg" className="bg-gradient-to-r from-violet-600 via-purple-600 to-fuchsia-600 hover:from-violet-700 hover:via-purple-700 hover:to-fuchsia-700 text-gray-800 shadow-lg shadow-purple-200 rounded-full">
                <Link to="/register">Start Your Journey <ArrowRight className="w-4 h-4 ml-2" /></Link>
              </Button>
              <Button asChild size="lg" variant="outline" className="border-2 border-purple-200 text-purple-600 hover:bg-purple-50 rounded-full">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer">Chat on WhatsApp</a>
              </Button>
            </div>
          </div>

          {/* Links Grid */}
          <div className="lg:col-span-7">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-8">
              <div>
                <h4 className="text-gray-800 font-semibold mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-gradient-to-r from-violet-500 to-purple-500 rounded-full"></span>
                  For Students
                </h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/courses" className="text-gray-500 hover:text-violet-600 transition-colors">Search Courses</Link></li>
                  <li><Link to="/destinations" className="text-gray-500 hover:text-violet-600 transition-colors">Destinations</Link></li>
                  <li><Link to="/scholarships" className="text-gray-500 hover:text-violet-600 transition-colors">Scholarships</Link></li>
                  <li><Link to="/check-eligibility" className="text-gray-500 hover:text-violet-600 transition-colors">Check Eligibility</Link></li>
                  <li><Link to="/register" className="text-gray-500 hover:text-violet-600 transition-colors">Apply Now</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-gray-800 font-semibold mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-gradient-to-r from-purple-500 to-fuchsia-500 rounded-full"></span>
                  For Partners
                </h4>
                <ul className="space-y-2 text-sm">
                  <li><Link to="/agent-register" className="text-gray-500 hover:text-purple-600 transition-colors">Become an Agent</Link></li>
                  <li><Link to="/blog" className="text-gray-500 hover:text-purple-600 transition-colors">Resources</Link></li>
                  <li><Link to="/training" className="text-gray-500 hover:text-purple-600 transition-colors">Training Hub</Link></li>
                  <li><Link to="/help" className="text-gray-500 hover:text-purple-600 transition-colors">Support</Link></li>
                </ul>
              </div>
              <div>
                <h4 className="text-gray-800 font-semibold mb-4 flex items-center gap-2">
                  <span className="w-2 h-2 bg-gradient-to-r from-fuchsia-500 to-pink-500 rounded-full"></span>
                  Contact
                </h4>
                <ul className="space-y-3 text-sm">
                  <li>
                    <a href="tel:+923268096371" className="text-gray-500 hover:text-fuchsia-600 transition-colors flex items-center gap-2">
                      <Phone className="w-4 h-4 text-violet-500" />
                      +92 326 8096371
                    </a>
                  </li>
                  <li>
                    <a href="mailto:info@hedirect.com" className="text-gray-500 hover:text-fuchsia-600 transition-colors flex items-center gap-2">
                      <Mail className="w-4 h-4 text-purple-500" />
                      info@hedirect.com
                    </a>
                  </li>
                  <li className="flex items-start gap-2 text-gray-500">
                    <MapPin className="w-4 h-4 text-fuchsia-500 mt-0.5" />
                    <span>Peshawar, PK<br/>Birmingham, UK</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-gray-100 bg-gradient-to-r from-violet-50/50 via-purple-50/50 to-fuchsia-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-gray-400 text-sm">© 2024 H E Direct Consultants. All rights reserved.</p>
            <div className="flex items-center gap-4">
              <a href={FACEBOOK_URL} className="w-10 h-10 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center text-gray-400 hover:bg-blue-500 hover:border-blue-500 hover:text-gray-800 hover:shadow-lg hover:shadow-blue-200 transition-all">
                <Facebook className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center text-gray-400 hover:bg-gradient-to-br hover:from-purple-500 hover:to-pink-500 hover:border-transparent hover:text-gray-800 hover:shadow-lg hover:shadow-purple-200 transition-all">
                <Instagram className="w-4 h-4" />
              </a>
              <a href="#" className="w-10 h-10 rounded-full bg-white border border-gray-100 shadow-sm flex items-center justify-center text-gray-400 hover:bg-blue-600 hover:border-blue-600 hover:text-gray-800 hover:shadow-lg hover:shadow-blue-200 transition-all">
                <Linkedin className="w-4 h-4" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default FooterOptionA;
